import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import * as _ from "lodash";
import { forkJoin, of, Subject } from "rxjs";
import { first } from "rxjs/operators";
import { CameraComponent } from "src/app/annotation/camera/camera.component";
import { NavAction } from "src/app/shared/top-nav/top-nav.component";
import { TokenService } from "../../../auth/token.service";
import { SurveyStatus } from "../../../shared/Models/enums/SurveySurveyStatus";
import { Survey } from "../../../shared/Models/read";
import { CompleteSurveyDto } from "../../../shared/Models/write/CompleteSurveyDto";
import { OutsideSurveyService } from "../../../shared/services/outside-survey.service";
import { ToastService } from "../../../shared/toast.service";
import { SurveyAbstractComponent } from "../shared/components/survey-abstract/survey-abstract.component";
import { DeviceCheckService } from "../shared/services/device-check.service";
import { FormValidationService } from "../shared/services/formValidation.service";
import { SavingStateService } from "../shared/services/saving-state.service";
import {
  OUTSIDE_SURVEY_ADDRESS_INFO,
  OUTSIDE_SURVEY_CONTACTS,
  OUTSIDE_SURVEY_OUTSIDE_INFO,
  OUTSIDE_SURVEY_OUTSIDE_UNITS,
  OUTSIDE_SURVEY_PHOTOS,
  OUTSIDE_SURVEY_VALIDATE,
  OUTSIDE_SURVEY_VAL_REMARKS,
} from "./config/outside-survey.magic-strings";
import { SessionService } from "../shared/services/session.service";
import { BuildingService } from "../../building-list/services/building.service";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";
import { SaveOutsideSurveyEngineerDto } from "src/app/shared/Models/write/SaveOutsideSurveyEngineerDto";
import { ChangeEngineerDto } from "src/app/shared/Models/write/ChangeEngineerDto";

@Component({
  selector: "app-outside-survey",
  templateUrl: "./outside-survey.component.html",
  styleUrls: ["./outside-survey.component.scss"],
})
export class OutsideSurveyComponent
  extends SurveyAbstractComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild("nav") protected tabs;
  @ViewChildren("outsideSurveyComponent")
  protected surveyComponents: QueryList<any>;

  @ViewChild("camera") camera: CameraComponent;
  @ViewChild("cameraLayer") cameraLayerRef: ElementRef<HTMLDivElement>;
  cameraLayer: HTMLDivElement;
  isPrefill: string;
  survey: Survey;
  surveyStatusEnum = SurveyStatus;
  isAuthCanWrite: boolean;
  tabConfig = {
    activeTabId: OUTSIDE_SURVEY_VAL_REMARKS,
    destroyOnHide: false,
    tabs: {
      [OUTSIDE_SURVEY_VAL_REMARKS]: {
        id: OUTSIDE_SURVEY_VAL_REMARKS,
        disabled: false,
        index: 0,
        error: false,
      },
      [OUTSIDE_SURVEY_ADDRESS_INFO]: {
        id: OUTSIDE_SURVEY_ADDRESS_INFO,
        disabled: false,
        index: 1,
        error: false,
      },
      [OUTSIDE_SURVEY_PHOTOS]: {
        id: OUTSIDE_SURVEY_PHOTOS,
        disabled: false,
        index: 2,
        error: false,
      },
      [OUTSIDE_SURVEY_OUTSIDE_UNITS]: {
        id: OUTSIDE_SURVEY_OUTSIDE_UNITS,
        disabled: false,
        index: 3,
        error: false,
      },
      [OUTSIDE_SURVEY_OUTSIDE_INFO]: {
        id: OUTSIDE_SURVEY_OUTSIDE_INFO,
        disabled: false,
        index: 4,
        error: false,
      },
      [OUTSIDE_SURVEY_CONTACTS]: {
        id: OUTSIDE_SURVEY_CONTACTS,
        disabled: false,
        index: 5,
        error: false,
      },
      [OUTSIDE_SURVEY_VALIDATE]: {
        id: OUTSIDE_SURVEY_VALIDATE,
        disabled: false,
        index: 6,
        error: false,
      },
    },
  };

  get canValidate() {
    return this.permissionsService.allowed(PermissionsEnum.ValidateSurvey);
  }

  get address() {
    return this.survey.building.getMainAddress();
  }
  get buildingKey() {
    return this.survey.building.lomKey;
  }
  get showValidationRemarksTab() {
    if (!this.survey) return true;

    return (
      ((this.survey || { validatorRemark: "" }).validatorRemark || "").length >
        0 && this.survey.surveyStatus >= SurveyStatus.OutsideOnGoing
    );
  }

  fullBlockAddress: string;
  names: string[];
  initials: string;
  userId = this.tokenService.getAuthUserId();

  constructor(
    private outsideSurveyService: OutsideSurveyService,
    protected toastService: ToastService,
    public tokenService: TokenService,
    protected savingStateService: SavingStateService,
    protected deviceCheckService: DeviceCheckService,
    private formValidationService: FormValidationService,
    protected sessionService: SessionService,
    private buildingService: BuildingService,
    private permissionsService: PermissionsService
  ) {
    super(
      tokenService,
      savingStateService,
      toastService,
      deviceCheckService,
      sessionService
    );
  }

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.setBlockAddress();
    this.isPrefill;
    this.names = [
      this.survey?.outsideEngineer?.firstName ?? "",
      this.survey?.outsideEngineer?.lastName ?? "",
    ];
    this.setInitials(this.names);
    this.setAuth();

    if (!this.showValidationRemarksTab)
      this.tabConfig.activeTabId = OUTSIDE_SURVEY_ADDRESS_INFO;

    this.formValidationService.clearForm();
    this.refreshValidateTab();
    this.navActions = this.initNavActions();
  }

  getErrors(tab: string): boolean {
    return this.formValidationService.getErrors(tab);
  }

  ngAfterViewInit() {
    this.cameraLayer = this.cameraLayerRef.nativeElement;

    if (this.survey.building)
      this.sessionService.setLocation(this.survey.building);
  }

  get validateTabVisible(): boolean {
    if (!this.survey) return false;

    if (!this.canValidate) return false;

    const status = this.survey.surveyStatus;
    const passingStatuses = [
      SurveyStatus.OutsideWaitingForValidation,
      SurveyStatus.OutsideDone,
      SurveyStatus.InsidePending,
    ];
    return _.includes(passingStatuses, status);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isAuthCanWrite) {
      await this.saveCurrentTab();
      return this.savingStateService.canDeactivateCheck();
    } else {
      return true;
    }
  }

  deactivate() {
    this.savingStateService.discardChanges();
  }

  navActions: NavAction[] = [];

  initNavActions(): NavAction[] {
    let list: NavAction[] = [];
    if (
      (this.permissionsService.allowed(PermissionsEnum.EditSurvey) &&
        (this.survey.surveyStatus === SurveyStatus.OutsideOnGoing ||
          this.survey.surveyStatus === SurveyStatus.Prefill)) ||
      (this.permissionsService.allowed(PermissionsEnum.ValidateSurvey) &&
        this.survey.surveyStatus === SurveyStatus.OutsideWaitingForValidation)
    )
      list.unshift(
        {
          icon: "exit_to_app",
          onClick: () => this.onSaveAndGoBack(),
          tooltip: "Save & exit",
        },
        {
          icon: "save",
          onClick: () => this.onSavePure(),
          tooltip: "Save",
        }
      );
    if (this.canSubmitSurvey())
      list.push({
        icon: "done",
        onClick: () => this.onCompleteSurvey(),
        style: "background: #5aea99; color: white",
        tooltip: "Submit",
      });

    if (this.permissionsService.allowed(PermissionsEnum.SurveyHistory))
      list.unshift({
        icon: "history",
        onClick: () => this.generateHistoryExport(),
        tooltip: "History Export",
      });
    return list;
  }

  private canSubmitSurvey(): boolean {
    var isPrefillAsBool = JSON.parse(this.isPrefill);
    if (
      this.isAuthCanWrite &&
      ((this.survey.surveyStatus === SurveyStatus.OutsideOnGoing &&
        !isPrefillAsBool) ||
        (this.survey.surveyStatus === SurveyStatus.OutsidePending &&
          !isPrefillAsBool))
    )
      return true;
    else return false;
  }

  private setAuth() {
    switch (this.survey.surveyStatus) {
      case (SurveyStatus.OutsidePending, SurveyStatus.Prefill):
        this.isAuthCanWrite = this.permissionsService.allowed(
          PermissionsEnum.PrefillSurvey
        );
      case SurveyStatus.OutsideOnGoing:
        this.isAuthCanWrite = this.permissionsService.allowed(
          PermissionsEnum.EditSurvey
        );
        break;
      case SurveyStatus.OutsideWaitingForValidation:
        this.isAuthCanWrite = this.permissionsService.allowed(
          PermissionsEnum.ValidateSurvey
        );
        break;
      default:
        this.isAuthCanWrite = false;
        break;
    }
  }

  performValidation(): boolean {
    const tabs = [...this.tabs.items];
    let validated = tabs.every((t) => !t.disabled);

    forkJoin(
      this.surveyComponents.map(
        (comp) => (comp.save && comp.save(true)) || of(true)
      )
    ).subscribe((b: boolean[]) => (validated &&= b.every((x) => x)));

    return validated;
  }

  async onCompleteSurvey() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@validation.photo.nointernet:No internet`,
        $localize`:@@validation.photo.nointernetcompletingsurvey:Completing survey is not possible while you're offline.`
      );
      return;
    }

    await this.saveCurrentTab();
    let validated = this.performValidation();

    if (validated) {
      const completeSurveyDto = new CompleteSurveyDto({
        surveyId: this.survey.id,
      });

      var isPrefillAsBool = JSON.parse(this.isPrefill);
      if (this.survey.outsideEngineer == null && !isPrefillAsBool)
        this.updateSurveyEngineer();

      this.savingStateService
        .saveAll(
          true,
          this.outsideSurveyService.completeSurvey(completeSurveyDto)
        )
        .pipe(first())
        .subscribe();
    } else {
      this.toastService.showErrorToast(
        $localize`:@@validation.survey.invalid:Not valid`,
        $localize`:@@validation.survey.invalidtabs:Not all tabs have been verified.`
      );
    }
  }

  async onSaveAndGoBack() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@validation.photo.nointernet:No internet`,
        $localize`:@@validation.photo.nointernetsave:Saving changes is not possible while you're offline.`
      );
      return;
    }

    var isPrefillAsBool = JSON.parse(this.isPrefill);
    if (this.survey.outsideEngineer == null && !isPrefillAsBool)
      this.updateSurveyEngineer();

    await this.saveCurrentTab();

    await this.savingStateService.saveAll(true).toPromise();
  }

  async onSavePure() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@validation.photo.nointernet:No internet`,
        $localize`:@@validation.photo.nointernetsave:Saving changes is not possible while you're offline.`
      );
      return;
    }

    var isPrefillAsBool = JSON.parse(this.isPrefill);
    if (this.survey.outsideEngineer == null && !isPrefillAsBool)
      this.updateSurveyEngineer();

    await this.saveCurrentTab();

    await this.savingStateService.saveAll(false).toPromise();
  }

  updateSurveyEngineer() {
    var outsideEngineer: ChangeEngineerDto = {
      engineerId: this.userId,
      surveyId: this.survey.id,
    };
    this.savingStateService.updateOutsideSurveyEngineer(outsideEngineer);
  }

  refreshValidateTab() {
    this.tabConfig.tabs.validate.disabled = _(this.tabConfig.tabs)
      .omit(OUTSIDE_SURVEY_VAL_REMARKS, OUTSIDE_SURVEY_VALIDATE)
      .some((x) => x.disabled);
  }

  generateHistoryExport() {
    this.buildingService
      .exportBuildingActionCsv({
        buildingIds: [this.survey.building.id],
      })
      .pipe(first())
      .subscribe(
        (res) => {
          this.downloadFile(res);
          this.toastService.showSuccesToast(
            $localize`:@@History.csv.triggered:csv has been triggered`,
            $localize`:@@general.csv:csv`
          );
        },
        (err) => {
          this.toastService.showErrorToast(
            $localize`:@@validation.somethingwhentwrong:Something went wrong!`,
            $localize`:@@admin.csv.somethingswrongelaborated:Something went wrong while generating the csv!`
          );
        }
      );
  }

  downloadFile(response: {
    fileName: string;
    fileType: string;
    data: ArrayBuffer;
  }) {
    let blob = new Blob([response.data], { type: response.fileType });

    const data = window.URL.createObjectURL(blob);
    var link = document.createElement("a");
    link.href = data;
    link.download = response.fileName;
    link.click();

    setTimeout(function () {
      // for Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(data);
    }, 100);
  }
}
